import {useCallback, useEffect, useRef, useState} from "react";
import axiosInstance from "../../config/axios-config";

const emptyPage = () => ({items: [], page: -1, last: true, total: 0});

export default function useCouponPharmacies(open, language) {
    const [search, setSearch] = useState("");
    const [result, setResult] = useState(emptyPage);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const scope = useRef(null);

    const fetchPage = useCallback(async (current, page) => {
        if (!current?.active || current.pending) return;
        current.pending = true;
        setLoading(true);
        setError(null);
        try {
            const {data} = await axiosInstance.get("/api/v1/tenants/pharmacies", {
                params: {searchTerm: current.search, page, size: 10}, signal: current.controller.signal,
                headers: {"Language-Context": (current.language || "en").toUpperCase()},
            });
            if (!Array.isArray(data.content)) throw new Error("Invalid pharmacy page");
            if (current.active) setResult(previous => ({page, last: data.last, total: data.totalElements,
                items: [...new Map([...(page ? previous.items : []), ...data.content].map(item => [item.id, item])).values()]}));
        } catch (failure) {
            if (current.active) {
                current.failedPage = page;
                setError(failure.response?.data?.message || "CouponPharmaciesLoadFailed");
            }
        } finally {
            current.pending = false;
            if (current.active) setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        const current = {active: true, search, language, pending: false, controller: new AbortController()};
        scope.current = current;
        setResult(emptyPage());
        setError(null);
        setLoading(true);
        const timer = setTimeout(() => fetchPage(current, 0), search ? 350 : 0);
        return () => { clearTimeout(timer); current.active = false; current.controller.abort(); };
    }, [open, search, language, fetchPage]);

    const changeSearch = value => {
        if (value === search) return;
        // Fence the old response immediately, including the debounce interval before the next request.
        if (scope.current) { scope.current.active = false; scope.current.controller.abort(); }
        setResult(emptyPage());
        setLoading(true);
        setError(null);
        setSearch(value);
    };

    return {...result, search, changeSearch, loading, error,
        retry: () => fetchPage(scope.current, scope.current?.failedPage ?? 0),
        loadMore: () => { if (result.page >= 0 && !result.last && !error) return fetchPage(scope.current, result.page + 1); }};
}
